import React, { useState, useEffect, useCallback } from 'react';
import './AdminVolunteers.css';
import { fetchWithAuth, isSuperAdmin, getBranchId } from '../utils/auth';
import { API_URL } from '../config';
import AdminModal from './AdminModal';

const emptyVolunteerForm = { id: null, name: '', personal_email: '', phone: '', student_id: null, student_label: '' };

const AdminVolunteers = () => {
  const superAdmin = isSuperAdmin();
  const defaultBranch = getBranchId() || 1;
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(defaultBranch);

  const [grades, setGrades] = useState([]);
  const [sections, setSections] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [volunteerSearch, setVolunteerSearch] = useState('');
  const [assignments, setAssignments] = useState([]);

  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [volunteerForm, setVolunteerForm] = useState(emptyVolunteerForm);
  const [showVolunteerForm, setShowVolunteerForm] = useState(false);
  const [studentQuery, setStudentQuery] = useState('');
  const [studentResults, setStudentResults] = useState([]);
  const [savingVolunteer, setSavingVolunteer] = useState(false);

  const [assignForm, setAssignForm] = useState({ volunteer_id: '', grade_id: '', section: '', role: 'main' });
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    if (superAdmin) {
      fetchWithAuth(`${API_URL}/api/admin/volunteers/branches`)
        .then(r => r.json()).then(d => setBranches(d.branches || [])).catch(() => {});
    }
    fetchWithAuth(`${API_URL}/api/admin/volunteers/grades`)
      .then(r => r.json()).then(d => setGrades(d.grades || [])).catch(() => {});
  }, [superAdmin]);

  const loadVolunteers = useCallback(async (search) => {
    try {
      const params = new URLSearchParams({ branch_id: selectedBranchId });
      if (search) params.set('search', search);
      const res = await fetchWithAuth(`${API_URL}/api/admin/volunteers?${params.toString()}`);
      const data = await res.json();
      if (res.ok) setVolunteers(data.volunteers || []);
    } catch { /* ignore */ }
  }, [selectedBranchId]);

  const loadAssignments = useCallback(async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/volunteers/assignments?branch_id=${selectedBranchId}`);
      const data = await res.json();
      if (res.ok) setAssignments(data.assignments || []);
    } catch { /* ignore */ }
  }, [selectedBranchId]);

  const loadSections = useCallback(async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/volunteers/sections?branch_id=${selectedBranchId}`);
      const data = await res.json();
      if (res.ok) setSections(data.sections || []);
    } catch { /* ignore */ }
  }, [selectedBranchId]);

  useEffect(() => { loadVolunteers(volunteerSearch); }, [volunteerSearch, selectedBranchId, loadVolunteers]);
  useEffect(() => { loadAssignments(); }, [selectedBranchId, loadAssignments]);
  useEffect(() => {
    setAssignForm(f => ({ ...f, section: '' }));
    loadSections();
  }, [selectedBranchId, loadSections]);

  const searchStudents = async (value) => {
    setStudentQuery(value);
    if (value.trim().length < 2) { setStudentResults([]); return; }
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/volunteers/student-search?value=${encodeURIComponent(value)}`);
      const data = await res.json();
      if (res.ok) setStudentResults(data.results || []);
    } catch { /* ignore */ }
  };

  const openNewVolunteer = () => {
    setVolunteerForm(emptyVolunteerForm);
    setStudentQuery('');
    setStudentResults([]);
    setShowVolunteerForm(true);
    setError('');
  };

  const openEditVolunteer = (v) => {
    setVolunteerForm({
      id: v.id, name: v.name, personal_email: v.personal_email || '', phone: v.phone || '',
      student_id: v.student_id, student_label: v.student_name || '',
    });
    setStudentQuery('');
    setStudentResults([]);
    setShowVolunteerForm(true);
    setError('');
  };

  const closeVolunteerForm = () => {
    setShowVolunteerForm(false);
    setError('');
  };

  const saveVolunteer = async () => {
    if (!volunteerForm.name.trim()) { setError('Name is required.'); return; }
    setSavingVolunteer(true);
    setError('');
    try {
      const body = {
        name: volunteerForm.name.trim(),
        personal_email: volunteerForm.personal_email.trim(),
        phone: volunteerForm.phone.trim(),
        student_id: volunteerForm.student_id,
      };
      const url = volunteerForm.id
        ? `${API_URL}/api/admin/volunteers/${volunteerForm.id}`
        : `${API_URL}/api/admin/volunteers`;
      const res = await fetchWithAuth(url, { method: volunteerForm.id ? 'PUT' : 'POST', body: JSON.stringify(body) });
      const data = await res.json();
      if (res.ok && data.success) {
        setShowVolunteerForm(false);
        setMessage(volunteerForm.id ? 'Volunteer updated.' : 'Volunteer added.');
        loadVolunteers(volunteerSearch);
        loadAssignments();
      } else {
        setError(data.error || 'Failed to save volunteer.');
      }
    } catch { setError('Network error.'); }
    setSavingVolunteer(false);
  };

  const deleteVolunteer = async (v) => {
    if (!window.confirm(`Remove ${v.name} from the volunteer directory?`)) return;
    setError('');
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/volunteers/${v.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (res.ok && data.success) {
        loadVolunteers(volunteerSearch);
      } else {
        setError(data.error || 'Failed to delete volunteer.');
      }
    } catch { setError('Network error.'); }
  };

  const createAssignment = async () => {
    setError(''); setMessage('');
    if (!assignForm.volunteer_id || !assignForm.grade_id || !assignForm.section) {
      setError('Pick a volunteer, grade, and section.');
      return;
    }
    setAssigning(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/volunteers/assignments`, {
        method: 'POST',
        body: JSON.stringify({ branch_id: selectedBranchId, ...assignForm }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setMessage('Assignment saved.');
        if (data.assignment?.warning) setError(data.assignment.warning);
        setAssignForm({ volunteer_id: '', grade_id: '', section: '', role: 'main' });
        loadAssignments();
      } else {
        setError(data.error || 'Failed to save assignment.');
      }
    } catch { setError('Network error.'); }
    setAssigning(false);
  };

  const removeAssignment = async (a) => {
    if (!window.confirm(`Remove ${a.volunteer.name} from Grade ${a.grade_name} — ${a.section}?`)) return;
    setError('');
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/volunteers/assignments/${a.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (res.ok && data.success) loadAssignments();
      else setError(data.error || 'Failed to remove assignment.');
    } catch { setError('Network error.'); }
  };

  // Group by grade+section so "Grade 3 — A1" reads as one class with its main teacher up top
  // and any subs underneath, instead of a flat unordered list of rows.
  const classGroups = assignments.reduce((groups, a) => {
    const key = `${a.grade_name || 'Grade'} — ${a.section}`;
    (groups[key] = groups[key] || []).push(a);
    return groups;
  }, {});
  Object.values(classGroups).forEach(rows => rows.sort((a, b) => (a.role === 'main' ? -1 : 1) - (b.role === 'main' ? -1 : 1)));

  return (
    <div className="volunteers-container">
      <div className="volunteers-header">
        <div>
          <h2>Volunteer Management</h2>
          <p className="volunteers-subtitle">
            Maintain the volunteer directory and assign a main (login-enabled) or sub teacher to each grade/section.
          </p>
        </div>
        {superAdmin && branches.length > 0 && (
          <select value={selectedBranchId} onChange={e => setSelectedBranchId(Number(e.target.value))} className="branch-select">
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
      </div>

      {error && !showVolunteerForm && <div className="error-banner">{error}</div>}
      {message && <p className="volunteers-success-msg">{message}</p>}

      <div className="form-card">
        <div className="volunteers-section-header">
          <h3>Volunteer Directory</h3>
          <button className="btn-primary" onClick={openNewVolunteer}>+ Add Volunteer</button>
        </div>
        <input
          type="text" className="volunteers-search" placeholder="Search by name or email…"
          value={volunteerSearch} onChange={e => setVolunteerSearch(e.target.value)}
        />

        {showVolunteerForm && (
          <AdminModal
            title={volunteerForm.id ? 'Edit Volunteer' : 'Add Volunteer'}
            onClose={closeVolunteerForm} onSubmit={saveVolunteer}
            saving={savingVolunteer} error={error}
          >
            <div className="form-row">
              <label>Name</label>
              <input type="text" value={volunteerForm.name} autoFocus
                     onChange={e => setVolunteerForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="form-row">
              <label>Personal email</label>
              <input type="text" value={volunteerForm.personal_email}
                     placeholder="Required if this person will be a main teacher"
                     onChange={e => setVolunteerForm(f => ({ ...f, personal_email: e.target.value }))} />
            </div>
            <div className="form-row">
              <label>Phone</label>
              <input type="text" value={volunteerForm.phone}
                     onChange={e => setVolunteerForm(f => ({ ...f, phone: e.target.value }))} />
            </div>
            <div className="form-row">
              <label>Parent of (optional)</label>
              {volunteerForm.student_id ? (
                <div className="linked-student">
                  <span>{volunteerForm.student_label} ({volunteerForm.student_id})</span>
                  <button type="button" className="btn-sm" onClick={() => setVolunteerForm(f => ({ ...f, student_id: null, student_label: '' }))}>Unlink</button>
                </div>
              ) : (
                <>
                  {/* Enter here would submit the whole dialog -- it's just a search box. */}
                  <input type="text" placeholder="Search student by name or ID…" value={studentQuery}
                         onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
                         onChange={e => searchStudents(e.target.value)} />
                  {studentResults.length > 0 && (
                    <ul className="student-search-results">
                      {studentResults.slice(0, 6).map(s => (
                        <li key={s.id}>
                          <button type="button" onClick={() => {
                            setVolunteerForm(f => ({ ...f, student_id: s.id, student_label: `${s.first_name} ${s.last_name}` }));
                            setStudentQuery(''); setStudentResults([]);
                          }}>
                            {s.first_name} {s.last_name} <span className="muted">({s.id})</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
          </AdminModal>
        )}

        {volunteers.length === 0 ? (
          <p className="muted">No volunteers found.</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr><th>Name</th><th>Personal Email</th><th>Phone</th><th>Parent Of</th><th></th></tr>
            </thead>
            <tbody>
              {volunteers.map(v => (
                <tr key={v.id}>
                  <td>{v.name}</td>
                  <td>{v.personal_email || <span className="muted">—</span>}</td>
                  <td>{v.phone || <span className="muted">—</span>}</td>
                  <td>{v.student_name || <span className="muted">—</span>}</td>
                  <td className="row-actions">
                    <button className="btn-sm" onClick={() => openEditVolunteer(v)}>Edit</button>
                    <button className="btn-sm btn-danger" onClick={() => deleteVolunteer(v)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="form-card">
        <h3>Assign to a Grade/Section</h3>
        <div className="assign-form-row">
          <select value={assignForm.volunteer_id} onChange={e => setAssignForm(f => ({ ...f, volunteer_id: e.target.value }))}>
            <option value="">Select volunteer…</option>
            {volunteers.map(v => <option key={v.id} value={v.id}>{v.name}{v.personal_email ? ` (${v.personal_email})` : ''}</option>)}
          </select>
          <select value={assignForm.grade_id} onChange={e => setAssignForm(f => ({ ...f, grade_id: e.target.value }))}>
            <option value="">Grade…</option>
            {grades.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
          <select value={assignForm.section} onChange={e => setAssignForm(f => ({ ...f, section: e.target.value }))}
                  disabled={sections.length === 0}>
            <option value="">{sections.length === 0 ? 'No sections yet' : 'Section…'}</option>
            {sections.map(s => (
              <option key={s.section} value={s.section}>{s.section}</option>
            ))}
          </select>
          <label className="role-radio">
            <input type="radio" name="assign_role" checked={assignForm.role === 'main'}
                   onChange={() => setAssignForm(f => ({ ...f, role: 'main' }))} /> Main
          </label>
          <label className="role-radio">
            <input type="radio" name="assign_role" checked={assignForm.role === 'sub'}
                   onChange={() => setAssignForm(f => ({ ...f, role: 'sub' }))} /> Sub
          </label>
          <button className="btn-primary" onClick={createAssignment} disabled={assigning}>
            {assigning ? 'Saving…' : 'Assign'}
          </button>
        </div>
        <p className="muted">
          Only one <strong>main</strong> teacher is allowed per grade/section per year — they get a login account,
          using the teacher email on file for that class if there's exactly one, otherwise their personal email.
          Any number of <strong>sub</strong>s can be added.
        </p>
      </div>

      <div className="form-card">
        <h3>Current Assignments</h3>
        {Object.keys(classGroups).length === 0 ? (
          <p className="muted">No assignments yet for this branch.</p>
        ) : (
          Object.entries(classGroups).sort(([a], [b]) => a.localeCompare(b)).map(([label, rows]) => (
            <div key={label} className="class-group">
              <div className="class-group-label">{label}</div>
              <ul className="class-group-rows">
                {rows.map(a => (
                  <li key={a.id}>
                    <span className={`role-badge role-${a.role === 'main' ? 'branch_admin' : 'teacher'}`}>{a.role}</span>
                    <span>{a.volunteer.name}</span>
                    {a.role === 'main' && a.login_email ? (
                      <span className="muted">
                        Login: {a.login_email}
                        {a.volunteer.personal_email && a.volunteer.personal_email !== a.login_email
                          ? ` · Personal: ${a.volunteer.personal_email}` : ''}
                      </span>
                    ) : (
                      a.volunteer.personal_email && <span className="muted">{a.volunteer.personal_email}</span>
                    )}
                    <button className="btn-sm btn-danger" onClick={() => removeAssignment(a)}>Remove</button>
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminVolunteers;
